import GeneralStatusLabel from "../../components/Common/GeneralStatusLabel";
import Helpers from "../../helpers/helpers";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, IconButton, InputAdornment, Stack, Box, Container, Card, CardContent } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Table from "../../components/Common/Table";
import Notification from "../../components/Common/Notification";
import PackageDialog from "./PackageDialog";
import CustomTextField from "../../components/Form/CustomTextField";
import CustomSelect2 from "../../components/Form/CustomSelect2";
import DatePicker from "../../components/Form/DatePicker";
import LoadingDialog from "../../components/Form/LoadingDialog";
import RenderPopOverButton from "../../helpers/RenderButton/RenderPopOverButton.js.js";
import useListingQuery, { positiveInteger } from "../../helpers/useListingQuery";
import { useCrud } from "../../store/crud";
import packageBookingsModule from "../../store/packageBookings/module";
import {
  appointmentDate, appointmentTime, assignedEmail, bookingId, bookingStatuses, downloadBookingsCsv,
  packageName, packageVariant, patientName,
} from "./bookingPresentation";
import { validateBookingDates } from "./bookingValidation";
import "./package-bookings.css";
const EMPTY_ROWS = [];
export default function PackageBookings() {
  const { query, update } = useListingQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = useSelector(root => root.Login.permissionArry) || [];
  const canEdit = permissions.includes("edit-package");
  const canExport = permissions.includes("export-package");
  const {
    list,
    confirm,
    export: exportBookings,
    state,
    loading,
    notification,
    setNotification
  } = useCrud(packageBookingsModule);
  const page = positiveInteger(query.get("page"));
  const length = [10, 20, 30, 40, 50].includes(Number(query.get("length"))) ? Number(query.get("length")) : 10;
  const search = query.get("search") || "";
  const status = query.get("status") || "";
  const date_from = query.get("date_from") || "";
  const date_to = query.get("date_to") || "";
  const package_id = query.get("package_id") || "";
  const scopedPackageName = package_id ? (query.get("package_name") || location.state?.packageName || `Package #${package_id}`) : "";
  const params = {
    page,
    length,
    search,
    status,
    date_from,
    date_to,
    package_id
  };
  const [draft, setDraft] = useState({ search, status, date_from, date_to });
  const [filterError, setFilterError] = useState("");
  const [confirmation, setConfirmation] = useState({
    open: false
  });
  const appliedError = validateBookingDates({ date_from, date_to });
  const listing = state.list.data;
  const rows = appliedError ? EMPTY_ROWS : listing?.rows || EMPTY_ROWS;
  const source = location.state?.packagesReturnTo;
  const returnTo = typeof source === "string" && /^\/packages(?:\?|$)/.test(source) ? source : "/packages";
  useEffect(() => {
    setDraft({ search, status, date_from, date_to });
    setFilterError("");
  }, [search, status, date_from, date_to]);
  useEffect(() => {
    if (!appliedError) list({
      page,
      length,
      search,
      status,
      date_from,
      date_to,
      package_id
    }, {
      preserveNotification: true
    });
  }, [list, page, length, search, status, date_from, date_to, package_id, appliedError]);
  const apply = event => {
    event.preventDefault();
    const error = validateBookingDates(draft);
    setFilterError(error);
    if (!error) update(draft, {
      resetPage: true
    });
  };
  const clear = () => {
    setDraft({
      search: "",
      status: "",
      date_from: "",
      date_to: ""
    });
    setFilterError("");
    update({
      search: "",
      status: "",
      date_from: "",
      date_to: ""
    }, {
      resetPage: true
    });
  };
  const openDetails = useCallback(row => {
    navigate('/packages/bookings/' + encodeURIComponent(row.id ?? row.booking_id), {
      state: {
        bookingsReturnTo: location.pathname + location.search,
        bookingsListState: location.state
      }
    });
  }, [navigate, location.pathname, location.search, location.state]);
  const openConfirmation = useCallback(row => {
    setConfirmation({
      open: true,
      id: row.id ?? row.booking_id,
      data: row
    });
  }, []);
  const confirmBooking = id => {
    if (!canEdit) return;
    confirm({
    id
  }, {
    successMessage: "Booking confirmed successfully",
    onSuccess: () => {
      setConfirmation({
        open: false
      });
      if (status === "pending" && page > 1 && rows.length === 1) update({
        page: page - 1
      });else list(params, {
        preserveNotification: true
      });
    }
    });
  };
  const change = field => event => setDraft(current => ({
    ...current,
    [field]: event.target.value === "all" ? "" : event.target.value
  }));
  const columns = useMemo(() => [{
    Header: "Booking ID",
    id: "id",
    width: "11%",
    accessor: row => <span className="booking-primary">{bookingId(row)}</span>
  }, {
    Header: "Patient",
    id: "patient",
    width: "16%",
    accessor: row => <span className="booking-primary">{patientName(row)}</span>
  }, {
    Header: "Package",
    id: "package",
    width: "22%",
    accessor: row => <Stack gap={1}>
      <span className="booking-truncate booking-primary" title={packageName(row)}>
          {packageName(row)}
      </span>
      <span className="booking-secondary">{packageVariant(row)}</span>
      </Stack>
  }, {
    Header: "Appointment Date",
    id: "appointment",
    width: "14%",
    accessor: row => {
      const date = appointmentDate(row);
      const time = appointmentTime(row);
      return <Stack gap={0.5}>
        <span>{date}</span>
        {time !== "-" && <span className="booking-secondary">{time}</span>}
      </Stack>;
    }
  }, {
    Header: "Assigned Email",
    id: "email",
    width: "20%",
    accessor: assignedEmail
  }, {
    Header: "Status",
    id: "status",
    width: "11%",
    accessor: row => <GeneralStatusLabel label={row.status} list={Helpers.bookingStatuses} />
  }, {
    Header: "",
    id: "actions",
    width: "6%",
    accessor: row => <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
      <IconButton size="small" aria-label={`View booking ${bookingId(row)}`} onClick={() => openDetails(row)}>
        <i className="ri-eye-line" />
      </IconButton>
      <RenderPopOverButton
        viewPermission={String(row.status).toLowerCase() === "pending" ? "edit-package" : undefined}
        viewLabel="Confirm Booking" viewIcon="ri-check-line" onView={() => openConfirmation(row)}
        editPermission="edit-package" editLabel="Manage Booking" onEdit={() => openDetails(row)}
        menuWidth={200}
      />
      </Stack>
  }], [openDetails, openConfirmation]);
  return <Box className="page-content package-bookings-page">
    <Container
      maxWidth={false} disableGutters
      sx={{
        px: 1.5
      }}
    >
      <Box className="package-bookings-heading">
        <IconButton className="bookings-back" component={Link} to={returnTo} aria-label="Back to packages">
          <i className="ri-arrow-left-line" />
        </IconButton>
        <Breadcrumbs
          title="Package Bookings"
          breadcrumbItems={[{
              title: "CVSKL",
              link: "/dashboard"
            }, {
              title: "Packages",
              link: returnTo
            }, {
              title: "Package Bookings",
              link: "#"
          }]}
        />
      </Box>
      <Notification notification={notification} setNotification={setNotification} />
      <PackageDialog
        content={confirmation} setContent={setConfirmation} type="confirm" data={confirmation.data}
        onConfirm={confirmBooking} loading={state.confirm.loading}
      />
      <LoadingDialog open={loading} />
      <Card
        sx={{
          boxShadow: "none"
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: "20px 16px",
              sm: "32px 24px 20px"
            },
            "&:last-child": {
              pb: 2.5
            }
          }}
        >
          <Stack
            className="bookings-toolbar" direction="row" alignItems="center" justifyContent="space-between"
            mb={3}
          >
            <select
              className="form-select" aria-label="Bookings per page" value={length}
              onChange={event => update({
                  length: event.target.value
                }, {
                  resetPage: true
              })}
            >
                {[10, 20, 30, 40, 50].map(size => <option key={size} value={size}>Show {size}</option>)}
            </select>
            {canExport && <Button
              color="light" outline className="bookings-export"
              disabled={Boolean(appliedError) || loading || !rows.length}
              onClick={() => exportBookings(params, {
                  onSuccess: downloadBookingsCsv
              })}
            >
              <i className="ri-download-line" />Export CSV
            </Button>}
          </Stack>
            {package_id && <Alert
            className="mb-3" severity="info"
            action={<Button color="link" onClick={() => update({
                  package_id: "",
                  package_name: ""
                }, {
                  resetPage: true
            })}>Show all</Button>}
          >
              Bookings for {location.state?.packageName || `package #${package_id}`}
          </Alert>}
          <Box
            className="bookings-filters" onSubmit={apply} component="form"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(220px, 1.8fr) minmax(140px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) auto auto"
              },
              gap: "10px",
              alignItems: "end",
              p: {
                xs: 2,
                sm: 3
              },
              mb: 3,
              border: 1,
              borderColor: "#dfe3e7",
              borderRadius: 2,
              bgcolor: "#f7f9fa"
            }}
          >
            <Box
              sx={{
                gridColumn: {
                  xs: "1 / -1",
                  sm: "auto"
                }
              }}
            >
              <CustomTextField
                title="Search" values={scopedPackageName || draft.search} placeholder="Search by patient or package"
                onChange={change("search")}
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                  <i className="ri-search-line" />
                  </InputAdornment>,
                  readOnly: Boolean(package_id),
                  inputProps: {
                    "aria-label": package_id ? `Bookings filtered by ${scopedPackageName}` : "Search by patient or package"
                  }
                }}
              />
            </Box>
            <Box>
              <CustomSelect2
                title="Status" values={draft.status || "all"} onChange={change("status")}
                data={[{
                    value: "all",
                    label: "All statuses"
                  }, ...bookingStatuses]}
              />
            </Box>
            <Box>
              <DatePicker title="Date From" values={draft.date_from} name="date_from" setFormValues={setDraft} />
            </Box>
            <Box>
              <DatePicker title="Date To" values={draft.date_to} name="date_to" setFormValues={setDraft} />
            </Box>
            <Button color="primary-button" type="submit">Filter</Button>
            <Button color="light" outline type="button" onClick={clear}>Reset</Button>
          </Box>
            {(filterError || appliedError) && <Alert severity="error" className="mb-3">
              {filterError || appliedError}
          </Alert>}
          <Box className="bookings-table management-table">
            <Table
              header={columns} data={rows} showEmptyHeader manualPagination
              emptyMessage={state.list.loading ? "Loading bookings..." : "No bookings found."} length={length}
              page={page} totalPage={listing?.lastPage || 1}
              getLengthPage={(nextPage, nextLength) => {
                if (nextPage !== page || Number(nextLength) !== length) {
                  update({
                      page: nextPage,
                      length: nextLength
                  });
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Container>
    </Box>;
}
